import { HttpErrorResponse } from '@angular/common/http';

import { LivErrorResponse } from '@app/core/models/response-protocol.model';

import { RemoteError, RemoteErrorSeverity } from '../models/remote-error.model';

const HTTP_STATUS_MESSAGES: Partial<Record<number, string>> = {
  400: 'Requisição inválida. Verifique os dados e tente novamente.',
  401: 'Acesso não autorizado. Faça login novamente.',
  403: 'Você não tem permissão para executar esta ação.',
  404: 'Recurso não encontrado.',
  409: 'Ocorreu um conflito com o estado atual.',
  429: 'Muitas requisições. Por favor, tente novamente mais tarde.',
  500: 'Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.',
  502: 'Bad gateway. Por favor, tente novamente mais tarde.',
  503: 'Serviço indisponível. Por favor, tente novamente mais tarde.'
};

const DEFAULT_REMOTE_ERROR_MESSAGE =
  'Ocorreu um erro inesperado. Por favor, tente novamente.';

export type GetRemoteErrorOptions = {
  /** Mensagem usada quando não há mensagem da API nem status mapeado */
  defaultMessage?: string;
  /** @deprecated Use defaultMessage */
  defaultMessageKey?: string;
};

function isLivErrorResponse(value: unknown): value is LivErrorResponse {
  const maybe = value as LivErrorResponse;
  return !!maybe && typeof maybe === 'object' && 'error' in maybe;
}

function mapSeverity(status: number): RemoteErrorSeverity {
  if (!status || status >= 500) {
    return 'danger';
  }
  return 'warning';
}

function resolveDefaultMessage(options: GetRemoteErrorOptions): string {
  return (
    options.defaultMessage ??
    options.defaultMessageKey ??
    DEFAULT_REMOTE_ERROR_MESSAGE
  );
}

function formatErrorMessage(message: unknown): string {
  if (!message) {
    return '';
  }

  if (typeof message === 'string') {
    return message;
  }

  if (Array.isArray(message)) {
    return message
      .map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
      .filter(Boolean)
      .join(', ');
  }

  return String(message);
}

function getHttpStatusMessage(status: number): string {
  return HTTP_STATUS_MESSAGES[status] ?? DEFAULT_REMOTE_ERROR_MESSAGE;
}

type ServerErrorShape = {
  message?: unknown;
  error?: unknown;
  detail?: unknown;
  errors?: unknown;
};

function formatErrorList(errors: unknown[]): string {
  return errors
    .map((err) => {
      if (typeof err === 'string') {
        return err;
      }
      if (err && typeof err === 'object') {
        const e = err as { message?: unknown; msg?: unknown };
        return (
          formatErrorMessage(e.message) ||
          formatErrorMessage(e.msg) ||
          JSON.stringify(err)
        );
      }
      return String(err);
    })
    .filter((v): v is string => Boolean(v))
    .join(', ');
}

function extractFromHttpError(
  error: HttpErrorResponse,
  defaultMessage: string
): string {
  if (error.status === 500) {
    return getHttpStatusMessage(500);
  }

  const serverError = error.error;
  if (typeof serverError === 'string') {
    return serverError || defaultMessage;
  }

  if (serverError && typeof serverError === 'object') {
    const se = serverError as ServerErrorShape;

    const direct =
      formatErrorMessage(se.message) ||
      formatErrorMessage(se.error) ||
      formatErrorMessage(se.detail);

    if (direct) {
      return direct;
    }

    if (Array.isArray(se.errors)) {
      return formatErrorList(se.errors);
    }
  }

  const statusMessage = getHttpStatusMessage(error.status);
  return error.message || statusMessage || defaultMessage;
}

function extractFromUnknownMessageShape(
  error: unknown,
  defaultMessage: string
): string {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const errorObj = error as { message: unknown };
    const formatted = formatErrorMessage(errorObj.message);
    if (formatted) {
      return formatted;
    }
  }

  return defaultMessage;
}

export function getRemoteErrorMessage(
  error: unknown,
  options: GetRemoteErrorOptions | string = {}
): string {
  const opts: GetRemoteErrorOptions =
    typeof options === 'string' ? { defaultMessage: options } : options;
  const defaultMessage = resolveDefaultMessage(opts);

  if (!error) {
    return defaultMessage;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof HttpErrorResponse) {
    return extractFromHttpError(error, defaultMessage);
  }

  if (isLivErrorResponse(error)) {
    const apiMessage = formatErrorMessage(error.error?.message);
    if (apiMessage) {
      return apiMessage;
    }
    return defaultMessage;
  }

  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  return extractFromUnknownMessageShape(error, defaultMessage);
}

export function getRemoteError(
  error: unknown,
  options: GetRemoteErrorOptions | string = {}
): RemoteError {
  const status = error instanceof HttpErrorResponse ? error.status : 0;
  const message = getRemoteErrorMessage(error, options);
  const severity = mapSeverity(status);

  const stack =
    error instanceof HttpErrorResponse
      ? error.error?.stack
      : isLivErrorResponse(error)
        ? error.error?.stack
        : error instanceof Error
          ? error.stack
          : undefined;

  return { status, message, severity, stack };
}
